"use client";

/**
 * Left Sidebar Navigation
 *
 * 220px expanded by default. Close button appears on hover.
 * Clicking collapsed rail (52px) re-expands. State persisted to localStorage.
 */

import { memo, useState, useCallback, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { destinationById, routeFor, type DestinationId } from "@bash/shared";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import { useCreditStore } from "@/stores/creditStore";
import { creditGaugeMax } from "@/lib/credit-gauge";
import { useSupportStore } from "@/stores/supportStore";
import { useInteractionsRealtime } from "@/hooks/useInteractionsRealtime";
import { useNeedsYouSeen } from "@/lib/needs-you-seen";
import { useComputerStore } from "@/stores/computerStore";
import { useSubscription } from "@/hooks/useSubscription";
import { useIsMobile } from "@/hooks/useIsMobile";
import { sizedAvatarUrl } from "@/lib/avatarUrl";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { HomeIcon } from "@/components/icons/HomeIcon";
import { AbilitiesIcon } from "@/components/icons/AbilitiesIcon";
import { KnowledgeIcon } from "@/components/icons/KnowledgeIcon";
import { RoutinesIcon } from "@/components/icons/RoutinesNavIcon";
import { ConnectorsIcon } from "@/components/icons/ConnectorsIcon";
import { SidebarSunIcon as SunIcon } from "@/components/icons/SidebarSunIcon";
import { SidebarMoonIcon as MoonIcon } from "@/components/icons/SidebarMoonIcon";
import { SidebarPanelIcon } from "@/components/icons/SidebarPanelIcon";
import { openCommandPalette } from "@/components/CommandPalette";
import { SidebarChevronRightIcon as ChevronRightIcon } from "@/components/icons/SidebarChevronRightIcon";
import { SupportIcon } from "@/components/icons/SupportIcon";
import { SearchIcon } from "@/components/icons/SearchIcon";
import { CloseIcon } from "@/components/icons/CloseIcon";

function formatPlanLabel(plan: string): string {
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/**
 * Credits bar = a fuel gauge, always in gauge language (founder, 08-03: never
 * brand-indigo): green while at least half remains, amber to 30%, orange to
 * 10%, red below that ("until X% left" ⇒ boundaries stay in the calmer band).
 * Grace period is red regardless. Green is softened — healthy should whisper.
 */
export function creditBarFill(
  remainingFraction: number,
  isGracePeriod: boolean
): string {
  if (isGracePeriod || remainingFraction < 0.1) return "bg-palette-red";
  if (remainingFraction < 0.3) return "bg-palette-orange";
  if (remainingFraction < 0.5) return "bg-palette-amber";
  return "bg-palette-green/70";
}

const SidebarNavItem = ({
  expanded,
  active,
  href,
  label,
  ariaLabel,
  icon,
  badge,
  badgeColor = "bg-palette-indigo",
  onClick,
}: {
  expanded: boolean;
  active?: boolean;
  href?: string;
  label: string;
  ariaLabel?: string;
  icon: React.ReactNode;
  badge?: string | number;
  /** Tailwind bg-* class for the badge pill — indigo by default (Autopilot's
   *  wash), Support wears amber to keep the two counts visually distinct. */
  badgeColor?: string;
  onClick: (() => void) | undefined;
}) => {
  const TagName: any = href ? Link : "button";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <TagName
            {...(href ? { href } : {})}
            aria-current={active ? "page" : undefined}
            aria-label={ariaLabel || label}
            className={cn(
              "relative flex items-center cursor-pointer group text-fg-default whitespace-nowrap",
              expanded
                ? "w-full h-8.5 gap-2.5 px-2.5"
                : "w-9 h-8.5 justify-center",
              active ? "bg-bg-muted" : "hover:bg-bg-muted",
              "rounded-8 transition-style-left-sidebar-inline-2"
            )}
            onClick={onClick}
          >
            <span className="flex-shrink-0">{icon}</span>
            {expanded && (
              <span className="text-[15px] font-normal">{label}</span>
            )}

            {badge && (
              <span
                className={cn(
                  "flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-2.5 font-semibold",
                  badgeColor,
                  "text-white",
                  !expanded ? "absolute -top-0.5 -right-0.5" : "ml-auto"
                )}
              >
                {badge}
              </span>
            )}
          </TagName>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={10}
          className={expanded ? "hidden" : ""}
        >
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Single source of truth for the sidebar's navigation targets: the destination
// registry (spec 2026-09-17 §5.3). The route list is exported for the shell's
// route warmer, rendering and warming must never drift apart (a separately
// hand-maintained warm list is how nav went cold before: it warmed
// /account/credits but not /account/profile).
// Nav order: Home → Abilities → Knowledge → Routines → Connectors.
// Skills + Agents live as tabs inside /library; Autopilot removed pending revamp.
// "Knowledge" is the section's word and stays local (the registry's knowledge.files
// label is the tab's "My files"); every other label is the destination's. The two
// hrefs that were redirect sources (/library, /knowledge) are now the pages they
// redirected to (R-P-6): the same landing, one hop fewer.
const NAV_DEFS: ReadonlyArray<{
  id: string;
  dest: DestinationId;
  label?: string;
  icon: React.ReactNode;
}> = [
  { id: "home", dest: "home", icon: <HomeIcon /> },
  { id: "library", dest: "library.abilities", icon: <AbilitiesIcon /> },
  {
    id: "knowledge",
    dest: "knowledge.files",
    label: "Knowledge",
    icon: <KnowledgeIcon />,
  },
  { id: "routines", dest: "routines", icon: <RoutinesIcon /> },
  { id: "apps", dest: "apps", icon: <ConnectorsIcon /> },
];

export const SIDEBAR_ROUTES: string[] = NAV_DEFS.map((d) => routeFor(d.dest));

interface UserInfo {
  id?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
}

interface LeftSidebarProps {
  currentView?: string;
  activeView?: "home" | "newTask" | "conversation";
  /** Fires after any sidebar <Link> click (the Link itself navigates); the shell uses it to close the mobile drawer. */
  onLinkNavigate?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** The task whose Computer is ON SCREEN right now, else null. The shell owns
   *  this (it holds the route and the selected task); the sidebar only obeys. */
  computerInView?: string | null;
  user?: UserInfo | null;
  onSignOut?: () => void;
}

export const LeftSidebar = memo(function LeftSidebar({
  currentView = "home",
  activeView,
  onLinkNavigate,
  isCollapsed: _isCollapsed,
  onToggleCollapse: _onToggleCollapse,
  computerInView = null,
  user,
  onSignOut,
}: LeftSidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const [userExpanded, setUserExpanded] = useState(true);

  // Sidebar visibility derives from two signals — the user's sticky
  // preference (`userExpanded`, persisted to localStorage) AND whether
  // any Computer panel is currently open. The Computer is the dominant
  // right-side surface; while it's open, the sidebar is force-collapsed.
  // When the Computer closes, the sidebar restores to the user's
  // preference. We deliberately do NOT mutate userExpanded from a
  // Computer transition useEffect — past attempts at that pattern raced
  // with the browser-session effect and the resize handler, leaving
  // `userExpanded` in inconsistent states depending on effect-firing
  // order. A pure derivation has no ordering surface.
  // ONE Computer holds the sidebar: the one you are looking at. This used to
  // read `Object.values(byTask).some(t => t.isOpen)` — ANY task's — and that
  // was two bugs at once (founder 09-04). A task you are not on auto-opens its
  // Computer when it writes a file (computerStore.onFileGenerated), so a
  // background task stole your sidebar wherever you stood; and because
  // `isOpen` is PERSISTED, a stale flag on a task you never revisit held the
  // sidebar shut for good — closing the Computer in front of you never brought
  // it back ("the biggest issue"). The shell hands down the task in view.
  // The Computer PANEL is the dominant right-side surface. A live
  // browser SESSION (sandbox/Steel) was previously also a gate, but
  // sessions outlive the panel — closing Computer doesn't end the
  // session, so the sidebar stayed force-collapsed even after the
  // user explicitly clicked the expand toggle. That made the toggle
  // look broken (the user's complaint that drove this fix). The
  // panel's open/closed flag is the only signal of "I'm looking at
  // Computer right now"; gate on that alone.
  // On mobile this same component renders inside AppShell's drawer overlay
  // (opened/closed externally via `showLeftSidebar`, not by this component's
  // own collapse state) — so it must always show FULL content there, whatever
  // `userExpanded` happened to persist from a desktop session.
  const isMobileViewport = useIsMobile();
  const isExpanded = (userExpanded && !computerInView) || isMobileViewport;

  // The one place the sidebar is opened or closed by hand.
  //
  // This logic came off the floating edge button when that was removed. It is
  // not decoration: when the sidebar is force-collapsed because a Computer is
  // open, clicking expand means "give me the sidebar back", and without the
  // close the click writes `userExpanded` while the derivation above keeps the
  // sidebar shut — the toggle looks broken. Only the Computer IN VIEW yields;
  // the sweep this replaced closed EVERY task's Computer, reaching into tasks
  // the user was not on (founder 09-04).
  const toggleSidebar = useCallback(() => {
    const expanding = !isExpanded;
    setUserExpanded(expanding);
    if (expanding && computerInView) {
      useComputerStore.getState().close(computerInView);
    }
  }, [isExpanded, computerInView]);

  // Fixed chrome centered on the CONTENT column (the home dock) needs the
  // sidebar's live width — a viewport-centered element can't see an in-flow
  // sibling collapse. Publish it as a root CSS custom property; consumers
  // derive their offset from it and glide on the same 0.2s width easing.
  // Values must match the w-(--sidebar-width-expanded) / w-(--sidebar-width-collapsed) classes on the nav below.
  // On mobile the sidebar is an overlay drawer occupying zero layout width,
  // so the variable must say 0px — otherwise every consumer (dock, preview
  // popup) offsets itself around a column that isn't there.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-sidebar-w",
      isMobileViewport
        ? "0px"
        : isExpanded
          ? "var(--sidebar-width-expanded)"
          : "var(--sidebar-width-collapsed)"
    );
    return () => {
      document.documentElement.style.removeProperty("--app-sidebar-w");
    };
  }, [isExpanded, isMobileViewport]);

  const balance = useCreditStore((s) => s.balance);
  const isGracePeriod = useCreditStore((s) => s.isGracePeriod);
  const isLoaded = useCreditStore((s) => s.isLoaded);
  const { plan, isLoaded: planLoaded } = useSubscription();
  const lastRechargeAmount = useCreditStore((s) => s.lastRechargeAmount);
  const spentSinceRecharge = useCreditStore((s) => s.spentSinceRecharge);
  const lifetimeEarned = useCreditStore((s) => s.lifetimeEarned);

  // Persist expanded state (only for wide viewports)
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth >= 1200) {
      localStorage.setItem("sidebar-expanded", String(userExpanded));
    }
  }, [userExpanded]);

  // Auto-collapse sidebar on narrow desktop viewports, restore preference on wide
  useEffect(() => {
    // Initial check on mount to prevent hydration mismatches
    if (window.innerWidth >= 768 && window.innerWidth < 1200) {
      setUserExpanded(false);
    } else {
      const stored = localStorage.getItem("sidebar-expanded");
      if (stored !== null) setUserExpanded(stored === "true");
    }

    const handleResize = () => {
      if (window.innerWidth >= 768 && window.innerWidth < 1200) {
        setUserExpanded(false);
      } else if (window.innerWidth >= 1200) {
        const stored = localStorage.getItem("sidebar-expanded");
        setUserExpanded(stored === null ? true : stored === "true");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Credit bar — % of the gauge's tank still on the desk, colored as a true
  // fuel gauge (creditBarFill). Tank sizing shared with CreditBar (credit-gauge).
  const maxBalance = creditGaugeMax(
    balance,
    spentSinceRecharge,
    lastRechargeAmount,
    lifetimeEarned
  );
  const barPct = Math.max(0, Math.min(100, (balance / maxBalance) * 100));
  const barFill = creditBarFill(barPct / 100, isGracePeriod);

  const firstName = (user?.name || "User").split(" ")[0];
  const isHomeActive = currentView === "home" && activeView === "home";
  const isSupportActive = currentView === "support";
  const supportUnread = useSupportStore((s) => s.getUnreadCount());
  // What waits on the person, counted on Home from every other page: the same
  // rows (and the same rail) as Home's Needs you tab, which says it on Home.
  // Only the asks they have not seen yet (on that tab, or on the ask's task)
  // count: once seen, the red stays off until a new one arrives.
  const { pending } = useInteractionsRealtime(user?.id ?? null);
  const seen = useNeedsYouSeen(user?.id ?? null);
  const unseen = pending.filter((p) => !seen.has(p.id)).length;
  const needsYou =
    unseen > 0 && !isHomeActive ? (unseen > 9 ? "9+" : unseen) : undefined;

  return (
    <div className="relative h-full">
      <nav
        aria-label="Main navigation"
        // Collapsed, the whole rail is the way back: any click that is not on
        // one of its buttons re-opens it, and the resize cursor says so before
        // the click. The file header has claimed this behaviour since the rail
        // shipped; it was never actually wired, so the empty strip has been
        // inert and only the (now removed) floating button opened it.
        //
        // The `closest` guard is what keeps New task, search and the mark
        // doing their own jobs — without it every button here would also
        // expand the sidebar underneath itself.
        onClick={
          isExpanded
            ? undefined
            : (e) => {
                const el = e.target as HTMLElement;
                if (el.closest("button, a, input, [role='button']")) return;
                toggleSidebar();
              }
        }
        className={cn(
          "relative flex flex-col h-full pb-4 border-r border-border-subtle bg-bg-subtle overflow-x-clip",
          isExpanded
            ? "w-(--sidebar-width-expanded)"
            : "w-(--sidebar-width-collapsed) cursor-e-resize",
          "transition-style-left-sidebar-inline"
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            "shrink-0 flex items-center gap-2 min-h-(--site-header-height) border-b border-border-subtle",
            isExpanded ? "px-2 justify-between" : "px-1.5 justify-center"
          )}
        >
          {/* Collapsed, the mark IS the way back: hovering swaps it for the
              panel icon and names the action. The rail already re-opened on
              click; this only makes an affordance that existed invisible no
              longer invisible. Expanded, it is just the logo. */}
          {isExpanded ? (
            <>
              <Link
                href={"/home"}
                prefetch={false}
                className="font-fauna font-[100] text-4.5 leading-none select-none translate-y-0.5 bg-wordmark-copper bg-clip-text text-transparent"
                onClick={onLinkNavigate}
              >
                VESTRA
              </Link>

              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon-md"
                  type="button"
                  title="Search"
                  aria-label="Search"
                  className="shrink-0"
                  onClick={() => {
                    onLinkNavigate?.();
                    openCommandPalette();
                  }}
                >
                  <SearchIcon className="w-4.5 h-4.5" />
                </Button>
                {isMobileViewport ? (
                  <Button
                    variant="ghost"
                    size="icon-md"
                    type="button"
                    title="Close sidebar"
                    aria-label="Close sidebar"
                    className="shrink-0"
                    onClick={onLinkNavigate}
                  >
                    <CloseIcon className="w-4.5 h-4.5" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon-md"
                    type="button"
                    title="Collapse sidebar"
                    aria-label="Collapse sidebar"
                    className="shrink-0"
                    onClick={toggleSidebar}
                  >
                    <SidebarPanelIcon className="w-4.5 h-4.5" />
                  </Button>
                )}
              </div>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              title="Open sidebar"
              aria-label="Open sidebar"
              className="group relative shrink-0"
              onClick={toggleSidebar}
            >
              <Image
                src="/vestra.svg"
                alt=""
                width={26}
                height={26}
                className="rounded-4 group-hover:opacity-0 transition-opacity duration-150"
              />
              <SidebarPanelIcon
                /* Stays 20px: this one replaces the 26px mark on the collapsed
                   rail, not a nav row. Matching the 18px header icons here
                   would read as lost in the space the logo leaves. */
                className="absolute w-5 h-5 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              />
            </Button>
          )}
        </div>

        <div
          className={`grow min-h-0 flex flex-col justify-between py-4 ${
            isExpanded ? "px-3" : "px-1.5"
          } overflow-y-auto overflow-x-clip`}
        >
          {/* Nav Items */}
          <div
            className={cn(
              "shrink-0 flex flex-col gap-0.5",
              isExpanded ? "" : "items-center"
            )}
          >
            {!isExpanded ? (
              <SidebarNavItem
                expanded={isExpanded}
                label={"Search"}
                ariaLabel={"Search"}
                icon={<SearchIcon className="w-4.5" />}
                onClick={openCommandPalette}
              />
            ) : null}
            {NAV_DEFS.map((item) => (
              <SidebarNavItem
                key={item.id}
                expanded={isExpanded}
                active={
                  item.id === "home" ? isHomeActive : currentView === item.id
                }
                href={routeFor(item.dest)}
                label={item.label ?? destinationById(item.dest)!.label}
                icon={item.icon}
                badge={item.id === "home" ? needsYou : undefined}
                badgeColor="bg-bh-brand"
                onClick={onLinkNavigate}
              />
            ))}
          </div>

          {/* Bottom — Support, theme toggle, User */}
          <div
            className={cn(
              "shrink-0 flex flex-col gap-0.5",
              isExpanded ? "" : "items-center"
            )}
          >
            {/* Support */}
            <SidebarNavItem
              expanded={isExpanded}
              active={isSupportActive}
              href={`/support`}
              label={"Support"}
              icon={<SupportIcon />}
              badge={
                supportUnread > 0
                  ? supportUnread > 9
                    ? "9+"
                    : supportUnread
                  : undefined
              }
              badgeColor="bg-palette-amber"
              onClick={onLinkNavigate}
            />

            {/* Theme Toggle */}
            <SidebarNavItem
              expanded={isExpanded}
              label={theme === "dark" ? "Light mode" : "Dark mode"}
              ariaLabel={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              icon={theme === "dark" ? <SunIcon /> : <MoonIcon />}
              onClick={toggleTheme}
            />

            {/* No Channels item (founder 09-02: "left panel should be same
                everywhere"). Channels connect from the home's own rail card;
                the sidebar reads identically on every screen. */}
          </div>
        </div>

        {/* User card — quiet elevated surface; credit meter stays indigo while
            healthy and only takes amber/red when the balance needs attention */}
        {user && (
          <div className={isExpanded ? "px-3" : "px-1.5"}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href="/account/profile"
                    aria-label={`${firstName} - Account`}
                    className={cn(
                      "relative rounded-12 transition-all duration-150 cursor-pointer overflow-x-clip group",
                      isExpanded
                        ? "block w-full"
                        : "w-9 h-9 flex items-center justify-center hover:bg-bg-muted"
                    )}
                    onClick={onLinkNavigate}
                  >
                    {!isExpanded ? (
                      <>
                        {user.avatarUrl ? (
                          <img
                            src={sizedAvatarUrl(user.avatarUrl, 28)}
                            alt={firstName}
                            loading="lazy"
                            decoding="async"
                            className="w-7 h-7 rounded-full object-cover ring-1 ring-border-subtle"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-3 font-medium bg-palette-indigo/15 text-palette-indigo ring-1 ring-border-subtle">
                            {firstName.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className={cn(
                          "rounded-16 p-2.5 transition-all duration-200",
                          "bg-bg-elevated border border-border-subtle",
                          "shadow-left-sidebar",
                          "group-hover:border-border-muted group-hover:shadow-left-sidebar-2"
                        )}
                      >
                        <div className="flex items-center gap-2.5">
                          {user.avatarUrl ? (
                            <img
                              src={sizedAvatarUrl(user.avatarUrl, 32)}
                              alt={firstName}
                              loading="lazy"
                              decoding="async"
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-border-subtle"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-3 font-medium flex-shrink-0 bg-palette-indigo/15 text-palette-indigo ring-1 ring-border-subtle">
                              {firstName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-3.5 font-medium leading-tight truncate text-fg-default text-left">
                                {firstName}
                              </span>
                              {planLoaded && (
                                <span className="text-2.5 font-semibold px-1.5 py-0.5 rounded-full bg-palette-indigo/10 text-palette-indigo uppercase tracking-wider flex-shrink-0">
                                  {formatPlanLabel(plan)}
                                </span>
                              )}
                            </div>
                            {isLoaded && (
                              <div className="mt-2">
                                <div className="h-1 rounded-full overflow-hidden bg-border-muted">
                                  <div
                                    className={cn(
                                      "h-full rounded-full transition-all duration-500",
                                      barFill,
                                      isGracePeriod && "animate-pulse"
                                    )}
                                    style={{ width: `${barPct}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          <ChevronRightIcon
                            stroke="var(--fg-faint)"
                            strokeWidth="2"
                            className="flex-shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200"
                          />
                        </div>
                      </div>
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  sideOffset={10}
                  className={isExpanded ? "hidden" : ""}
                >
                  {firstName}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </nav>
    </div>
  );
});
